import { inject, Injectable } from '@angular/core';
import { Model } from '@df/catalog/model';
import { app } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { ucfirst } from '@df/util/fn/ucfirst.fn';
import { EShortcatsSection } from '@icms/plugin/shortcuts/icms-shortcuts.provider';
import { SubscriptionLike } from 'rxjs';
import { SsrApiService } from './ssr-api.service';

@Injectable({
    providedIn: 'root'
})
export class SsrShortcutsService {
    private ssrApiService = inject(SsrApiService);
    private dfEventService = inject(DfEventService);
    private subscription?: SubscriptionLike;

    appInitializer() {
        this.subscription = this.dfEventService.before(event => {
            if (event.id === 'iCmsProvide' && event.data.providerId === 'shortcuts') {
                const target = event.data.value.find(item => item.title === EShortcatsSection.SystemActions);
                const model = event.data.arg as Model;
                if (model && model.type && model.id) {
                    const invalidateStatus = new Status();
                    target.children.push({
                        command: `Invalidate SSR cache`,
                        info: `for ${model.type} ${model.id}, both universal and prererender`,
                        status: invalidateStatus,
                        action: () => {
                            this.wrapRequest(
                                () =>
                                    this.ssrApiService.flushByTag(invalidateStatus, {
                                        type: model.type,
                                        ids: [model.id],
                                        flush: false
                                    }),
                                `${ucfirst(model.type)} ${model.id} SSR cache invalidated`,
                                `Failed to invalidated ${model.type} ${model.id} SSR cache`
                            );
                        }
                    });

                    const uniStatus = new Status();
                    target.children.push({
                        command: `Flush universal`,
                        info: 'all pages cache',
                        status: uniStatus,
                        action: () => {
                            this.wrapRequest(
                                () => this.ssrApiService.flushUniversalCache(uniStatus),
                                'Universal cache flushed',
                                `Failed to flush ${model.type} ${model.id} cache`
                            );
                        }
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    async wrapRequest(action: () => Promise<any>, successMsg: string, errorMsg: string) {
        try {
            await action();
            app.iCmsNotificationService.add({
                title: successMsg,
                type: 'success'
            });
        } catch (err) {
            app.iCmsNotificationService.add({
                title: errorMsg,
                type: 'error',
                copy: app.iCmsNotificationService.toErrorMsg(err),
                duration: 0
            });
        }
    }
}
